// Una sección de la ficha de la póliza cuyos campos se pueden ordenar
// (Lissette, 2026-10-02). La lógica vive en ../ordenDeCampos.js.
//
// El orden es de TODA LA AGENCIA (Lissette 2026-10-03: "hazlo de toda la
// agencia"): se guarda en `agency_settings.policy_card_layout` y lo ven igual
// todas las personas de la agencia, en todas las pólizas de ese tipo. Salió un
// día guardado por persona (en `uiPrefs`); ese orden personal ya no se lee.
//
// Cada hijo con la prop `campo` se puede mover; lo que no la lleva (el título
// de la sección) se queda delante. En modo edición cada campo lleva un asa y un
// borde punteado, se arrastra con el ratón, y en el teléfono —donde arrastrar
// no funciona— se mueve con las flechas.
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAgencySettings, updateAgencySettings } from '../api.js';
import { IS_DEMO } from '../demoMode.js';
import { claveDeOrden, aplicarOrden, moverCampo, pasoDeCampo } from '../ordenDeCampos.js';

// Lo que comparte la tarjeta con sus secciones: si se está editando y dónde
// quedan los borradores hasta que se pulsa Guardar.
export const OrdenDeFichaContext = createContext(null);

// ── El orden de la agencia, una sola copia para todas las tarjetas ─────────
// Se pide UNA vez (no una por tarjeta) y, al guardar, todas las tarjetas
// abiertas se repintan con el aviso de abajo.
const EVENTO = 'deskte:ordenFicha';
let ordenes = {};
let pedido = null;
const avisar = () => window.dispatchEvent(new Event(EVENTO));

function cargarOrdenes() {
  if (!pedido) {
    pedido = (IS_DEMO ? Promise.resolve({}) : getAgencySettings())
      .then((s) => {
        const o = s && s.policyCardLayout;
        ordenes = o && typeof o === 'object' && !Array.isArray(o) ? o : {};
        avisar();
      })
      .catch(() => { pedido = null; });  // se reintenta en la siguiente tarjeta
  }
  return pedido;
}

// Guarda en la agencia. Si el servidor dice que no, se deshace lo de pantalla
// y el error SUBE, para que la tarjeta lo diga: un orden que se ve guardado y
// al recargar vuelve atrás es justo lo que se quiere evitar.
async function escribir(siguiente) {
  const antes = ordenes;
  ordenes = siguiente;
  avisar();
  if (IS_DEMO) return;
  try {
    await updateAgencySettings({ policyCardLayout: siguiente });
  } catch (e) {
    ordenes = antes;
    avisar();
    throw e;
  }
}

export function guardarOrdenes(borradores) {
  return escribir({ ...ordenes, ...borradores });
}

export function restablecerOrdenes(claves) {
  const siguiente = { ...ordenes };
  claves.forEach((clave) => { delete siguiente[clave]; });
  return escribir(siguiente);
}

export function CamposOrdenables({ tipo, seccion, style, children }) {
  const ctx = useContext(OrdenDeFichaContext);
  const editando = !!(ctx && ctx.editando);
  const clave = claveDeOrden(tipo, seccion);
  const [, setVersion] = useState(0);
  useEffect(() => {
    const repintar = () => setVersion((v) => v + 1);
    window.addEventListener(EVENTO, repintar);
    cargarOrdenes();
    return () => window.removeEventListener(EVENTO, repintar);
  }, []);

  const hijos = React.Children.toArray(children).filter(Boolean);
  const fijos = hijos.filter((h) => !(h.props && h.props.campo));
  const movibles = hijos.filter((h) => h.props && h.props.campo);
  const porCampo = new Map(movibles.map((h) => [h.props.campo, h]));
  const deFabrica = movibles.map((h) => h.props.campo);

  const guardado = aplicarOrden(deFabrica, Array.isArray(ordenes[clave]) ? ordenes[clave] : null);
  if (ctx) ctx.secciones.current.add(clave);
  const borrador = editando && ctx.borradores[clave] ? aplicarOrden(deFabrica, ctx.borradores[clave]) : guardado;
  const cambiar = (nueva) => ctx.setBorrador(clave, nueva);

  const [arrastrado, setArrastrado] = useState(null);

  const pintar = (campo) => {
    const hijo = porCampo.get(campo);
    if (!editando) return hijo;
    const ancho = hijo.props.wide;
    return (
      <div
        key={campo}
        draggable
        onDragStart={(e) => { setArrastrado(campo); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', campo); } catch { /* algunos navegadores */ } }}
        onDragOver={(e) => { e.preventDefault(); if (arrastrado && arrastrado !== campo) cambiar(moverCampo(borrador, arrastrado, campo)); }}
        onDrop={(e) => e.preventDefault()}
        onDragEnd={() => setArrastrado(null)}
        className={`dkt-campo-movible${arrastrado === campo ? ' dkt-campo-arrastrado' : ''}`}
        style={{ gridColumn: ancho ? '1/-1' : 'auto' }}
      >
        <div className="dkt-campo-asa" aria-hidden="true">⠿</div>
        {hijo}
        <div className="dkt-campo-flechas">
          <button type="button" onClick={() => cambiar(pasoDeCampo(borrador, campo, -1))} aria-label="Mover antes">‹</button>
          <button type="button" onClick={() => cambiar(pasoDeCampo(borrador, campo, 1))} aria-label="Mover después">›</button>
        </div>
      </div>
    );
  };

  return (
    <div style={style}>
      {fijos}
      {borrador.map(pintar)}
    </div>
  );
}
